<template>
  <div class="mobilePanel" :style="{ height: panelHeight + 'px' }">
    <div
      class="mobilePanel__handle"
      @touchstart.stop="onTouchStart"
      @touchmove.stop.prevent="onTouchMove"
      @touchend.stop="onTouchEnd"
      @click.stop="onHandleTap"
    >
      <div class="mobilePanel__handle__bar"></div>
      <div class="mobilePanel__handle__row">
        <div class="mobilePanel__handle__title">
          {{ $t("app.home.map_store_query_title") }}
          <span v-if="totalCount" class="mobilePanel__count"
            >({{ totalCount }})</span
          >
        </div>
        <button
          type="button"
          class="mobilePanel__overviewBtn"
          @click.stop="onOverview"
        >
          <van-icon name="replay" class="mobilePanel__overviewBtn__icon" />
          <span>{{ $t("app.home.map_store_overview_button") }}</span>
        </button>
      </div>
    </div>

    <div class="mobilePanel__searchWrap">
      <van-field
        class="mobilePanel__search"
        v-model.trim="innerKeyword"
        :clearable="false"
        :placeholder="$t('app.home.map_store_search_store_or_code_placeholder')"
      >
        <template #left-icon>
          <span class="mobilePanel__searchIconWrap">
            <img class="mobilePanel__searchIcon" :src="iconSearch" alt="" />
          </span>
        </template>
        <template #button>
          <van-icon
            v-show="innerKeyword && String(innerKeyword).length > 0"
            name="cross"
            class="mobilePanel__searchClear"
            @click.stop="onClearKeyword"
          />
        </template>
      </van-field>
    </div>

    <div v-if="loading" class="mobilePanel__loading">
      {{ $t("app.home.map_loading") }}
    </div>

    <div
      v-else-if="filteredGroups.length === 0"
      class="mobilePanel__empty"
    >
      <p class="mobilePanel__emptyLine1">
        {{ $t("app.home.map_store_empty_area_line1") }}
      </p>
      <p class="mobilePanel__emptyLine2">
        {{ $t("app.home.map_store_empty_area_line2") }}
      </p>
    </div>

    <div v-else class="mobilePanel__list" @touchmove.stop>
      <div
        v-for="(country, cIdx) in filteredGroups"
        :key="`${country.countryName}-${cIdx}`"
        class="mobilePanel__country"
      >
        <div class="mobilePanel__countryTitle">
          {{ country.countryName }}
        </div>

        <div
          v-for="(province, pIdx) in country.provinces"
          :key="`${country.countryName}-${province.provinceName}-${pIdx}`"
          class="mobilePanel__province"
        >
          <div class="mobilePanel__provinceTitle">
            <span>{{ province.provinceName }}</span>
            <span class="mobilePanel__provinceCount">{{
              province.dealers.length
            }}</span>
          </div>

          <div
            v-for="item in province.dealers"
            :key="item.__listKey"
            class="store"
            :class="{ 'store--active': item.__listKey === activeKey }"
            @click="onSelect(item)"
          >
            <div class="store__head">
              <div class="store__name">{{ item.fullName || "-" }}</div>
            </div>
            <div class="store__main">
              <div class="store__rows">
                <div class="store__row" v-if="getDealerDisplayAddress(item)">
                  <img class="store__row__location" :src="iconLoc" alt="" />
                  <div class="store__row__text">
                    {{ getDealerDisplayAddress(item) }}
                  </div>
                  <button
                    type="button"
                    class="store__copyBtn"
                    @click.stop="onCopyAddress(item)"
                  >
                    {{ $t("app.home.map_popup_copy_address") }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, watch, onMounted, onBeforeUnmount } from "vue";
import iconLoc from "@/assets/map-icons/map_location.png";
import iconSearch from "@/assets/map-icons/map_search.png";
import { getDealerDisplayAddress } from "@/views/home/maptiler/useMapTilerAllStores";
import { useMapCountryGroupsFilter } from "@/views/home/maptiler/useMapCountryGroupsFilter";
import { useMapTilerCopyText } from "@/views/home/maptiler/useMapTilerCopyText";

export default {
  name: "MapTilerMobileAllStoresPanel",
  props: {
    groups: { type: Array, default: () => [] },
    keyword: { type: String, default: "" },
    activeKey: { type: [String, Number], default: null },
    loading: { type: Boolean, default: false },
  },
  emits: ["update:keyword", "select", "overview", "height-change"],
  setup(props, { emit, expose }) {
    const { innerKeyword, filteredGroups, totalCount, onClearKeyword } =
      useMapCountryGroupsFilter(props, emit);
    const panelHeight = ref(0);
    const minHeight = ref(0);
    const maxHeight = ref(0);
    const dragging = ref(false);
    let startY = 0;
    let startHeight = 0;

    const initHeights = () => {
      const vh = window.innerHeight || 667;
      maxHeight.value = Math.round(vh * 0.58);
      const rootFontSize = parseFloat(
        getComputedStyle(document.documentElement).fontSize || "16"
      );
      minHeight.value = Math.round(rootFontSize * 1.12);
      if (!panelHeight.value) {
        panelHeight.value = minHeight.value;
      }
      emit("height-change", panelHeight.value);
    };

    watch(panelHeight, (h) => emit("height-change", h));

    const { copyDealerAddress } = useMapTilerCopyText();

    const onCopyAddress = (item) => {
      copyDealerAddress(item);
    };


    const onSelect = (item) => {
      emit("select", { item, key: item.__listKey });
    };

    const collapseToMin = () => {
      if (!minHeight.value) initHeights();
      panelHeight.value = minHeight.value;
    };

    const onOverview = () => {
      collapseToMin();
      emit("overview");
    };

    const onTouchStart = (event) => {
      if (!maxHeight.value || !minHeight.value) initHeights();
      const touch = event.touches[0];
      dragging.value = true;
      startY = touch.clientY;
      startHeight = panelHeight.value;
    };

    const onTouchMove = (event) => {
      if (!dragging.value) return;
      const touch = event.touches[0];
      const delta = startY - touch.clientY;
      let next = startHeight + delta;
      if (next < minHeight.value) next = minHeight.value;
      if (next > maxHeight.value) next = maxHeight.value;
      panelHeight.value = next;
    };

    const onTouchEnd = () => {
      if (!dragging.value) return;
      dragging.value = false;
      const mid = (minHeight.value + maxHeight.value) / 2;
      panelHeight.value =
        panelHeight.value > mid ? maxHeight.value : minHeight.value;
    };

    const onHandleTap = () => {
      if (!maxHeight.value || !minHeight.value) initHeights();
      const mid = (minHeight.value + maxHeight.value) / 2;
      panelHeight.value =
        panelHeight.value > mid ? minHeight.value : maxHeight.value;
    };

    onMounted(() => {
      initHeights();
      window.addEventListener("resize", initHeights);
    });

    onBeforeUnmount(() => {
      window.removeEventListener("resize", initHeights);
    });

    expose({ collapseToMin });

    return {
      innerKeyword,
      filteredGroups,
      totalCount,
      iconLoc,
      iconSearch,
      panelHeight,
      onClearKeyword,
      onSelect,
      onOverview,
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      onHandleTap,
      getDealerDisplayAddress,
      onCopyAddress,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "@/style/mixins.scss";
@import "@/style/viriables.scss";

$mobile-aspect-ratio: 9 / 19.5;

.mobilePanel {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  margin: 0 auto;
  max-width: 100vw;
  @media (min-width: 768px) {
    max-width: min(calc(100vh * #{$mobile-aspect-ratio}), 100vw);
  }
  background: #fff;
  border-top-left-radius: 0.16rem;
  border-top-right-radius: 0.16rem;
  box-shadow: 0 -0.06rem 0.18rem rgba(0, 0, 0, 0.08);
  z-index: 1950;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: height 0.28s ease;

  &__handle {
    flex-shrink: 0;
    padding: 0.08rem 0.14rem 0.1rem;

    &__bar {
      width: 0.36rem;
      height: 0.04rem;
      border-radius: 0.04rem;
      background: #d8d8d8;
      margin: 0 auto;
    }

    &__row {
      margin-top: 0.1rem;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.08rem;
    }

    &__title {
      flex: 1;
      min-width: 0;
      text-align: left;
      font-size: 0.15rem;
      font-weight: 700;
      color: $title-fontColor;
    }
  }

  &__count {
    margin-left: 0.04rem;
    font-size: 0.12rem;
    font-weight: 500;
    color: #888;
  }

  &__overviewBtn {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 0.04rem;
    height: 0.28rem;
    padding: 0 0.1rem;
    border: 1px solid rgba(0, 147, 67, 0.35);
    border-radius: 999px;
    background: rgba(0, 147, 67, 0.08);
    color: $themeColor;
    font-size: 0.11rem;
    font-weight: 600;
    line-height: 1;

    &__icon {
      font-size: 0.12rem;
    }
  }

  &__searchWrap {
    flex-shrink: 0;
    padding: 0 0.14rem 0.08rem;
    box-sizing: border-box;
  }

  &__search {
    width: 100%;
    box-sizing: border-box;
    border: 1px solid #e8e8e8;
    border-radius: 999px;
    background: #fafafa;
    overflow: hidden;
  }

  &__searchIconWrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 0.18rem;
    height: 0.18rem;
    flex-shrink: 0;
  }

  &__searchIcon {
    width: 0.16rem;
    height: 0.16rem;
    display: block;
    object-fit: contain;
  }

  &__searchClear {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #999;
    font-size: 0.16rem;
    line-height: 1;
  }

  &__loading,
  &__empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0.24rem;
    text-align: center;
    color: #888;
    font-size: 0.12rem;
  }

  &__emptyLine1 {
    margin: 0;
    font-size: 0.13rem;
    color: #666;
  }

  &__emptyLine2 {
    margin: 0.08rem 0 0;
    font-size: 0.12rem;
    color: #999;
  }

  &__list {
    flex: 1;
    min-height: 0;
    overflow-y: scroll;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-y;
    overscroll-behavior-y: contain;
    padding: 0 0.14rem 0.12rem;
  }

  &__countryTitle {
    margin: 0.1rem 0 0.08rem;
    padding-left: 0.04rem;
    font-size: 0.13rem;
    font-weight: 700;
    color: $themeColor;
    border-left: 0.03rem solid $themeColor;
    text-align: left;
  }

  &__province {
    margin-bottom: 0.12rem;
  }

  &__provinceTitle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.08rem;
    margin: 0.12rem 0 0.08rem;
    padding: 0.08rem 0.1rem;
    border-radius: 0.08rem;
    border-left: 0.04rem solid $themeColor;
    background: linear-gradient(90deg, rgba(0, 147, 67, 0.12) 0%, #f5f7f6 100%);
    font-size: 0.13rem;
    font-weight: 700;
    color: #2b2b2b;
    line-height: 1.2;
    text-align: left;
  }

  &__provinceCount {
    flex-shrink: 0;
    min-width: 0.22rem;
    height: 0.22rem;
    padding: 0 0.07rem;
    border-radius: 0.11rem;
    background: $themeColor;
    color: #fff;
    font-size: 0.11rem;
    font-weight: 600;
    line-height: 0.22rem;
    text-align: center;
  }
}

.store {
  position: relative;
  margin-bottom: 0.08rem;
  padding: 0.12rem;
  border-bottom: 1px solid #ebebeb;
  background: #fff;
  text-align: left;

  &--active {
    background: rgba(0, 147, 67, 0.12);
  }

  &--active::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 0.04rem;
    background: #009343;
  }

  &__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.08rem;
  }

  &__name {
    flex: 1;
    font-size: 0.14rem;
    font-weight: 600;
    color: #333;
    line-height: 1.3;
    @include ellipsis-two;
  }

  &__main {
    margin-top: 0.08rem;
  }

  &__rows {
    min-width: 0;
  }

  &__row {
    display: flex;
    gap: 0.06rem;
    align-items: flex-start;

    &__location {
      width: 0.14rem;
      height: 0.14rem;
      margin-top: 0.02rem;
      flex-shrink: 0;
    }

    &__text {
      flex: 1;
      font-size: 0.12rem;
      color: #666;
      line-height: 1.35;
      word-break: break-all;
    }
  }

  &__copyBtn {
    flex-shrink: 0;
    border: none;
    background: rgba(0, 147, 67, 0.08);
    color: $themeColor;
    font-size: 0.1rem;
    line-height: 1;
    padding: 0.05rem 0.08rem;
    border-radius: 0.04rem;
    cursor: pointer;
  }
}

::v-deep(.mobilePanel__search.van-cell) {
  padding: 0 0.1rem;
  background: transparent;
  overflow: hidden;
  align-items: center;
  min-height: 0.36rem;
  height: 0.36rem;
}

::v-deep(.mobilePanel__search .van-cell__value) {
  overflow: hidden;
  display: flex;
  align-items: center;
}

::v-deep(.mobilePanel__search .van-field__body) {
  align-items: center;
  min-width: 0;
  width: 100%;
  height: 0.36rem;
}

::v-deep(.mobilePanel__search .van-field__left-icon) {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.06rem;
  height: 0.36rem;
  line-height: 1;
}

::v-deep(.mobilePanel__search .van-field__control) {
  font-size: 0.12rem;
  line-height: 0.36rem;
  height: 0.36rem;
  padding: 0;
  min-width: 0;
}

::v-deep(.mobilePanel__search .van-field__button) {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding-left: 0.04rem;
  padding-right: 0;
  height: 0.36rem;
  line-height: 1;
}
</style>
